import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Target, ChevronRight } from "lucide-react";

/**
 * Compact per-player assessment-progress widget for the Coach Dashboard.
 * Shows the top 6 players closest to levelling up — quick "who's a tick or two away" view.
 * Source: GET /coach/assessment-progress (sorted by % descending).
 */
export default function AssessmentProgressWidget() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/coach/assessment-progress");
        setRows(data || []);
      } catch (_e) { /* silent — widget hides */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return null;
  if (!rows.length) return null;

  const top = rows.slice(0, 6);
  const aboutToLevel = rows.filter((r) => r.total > 0 && r.ticked >= r.total).length;

  return (
    <section className="card-surface p-6 fade-up" data-testid="assessment-progress-widget">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="label-mono mb-1" style={{ color: "#CCFF00" }}>
            <Target className="w-3.5 h-3.5 inline mr-1" /> ASSESSMENT PROGRESS
          </div>
          <h2 className="h-display text-2xl">Who's closest to levelling up</h2>
          <p className="text-xs text-[#A3A3A3] mt-1">
            {aboutToLevel > 0
              ? <><span className="text-[#CCFF00] font-bold">{aboutToLevel}</span> player{aboutToLevel === 1 ? "" : "s"} ready to advance now.</>
              : "Top players sorted by checklist completion at their target level."}
          </p>
        </div>
        <Link to="/players" data-testid="ap-view-all" className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00] inline-flex items-center gap-1">
          All players <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <ul className="divide-y divide-[#262626]">
        {top.map((r) => {
          const ready = r.total > 0 && r.ticked >= r.total;
          const colour = ready ? "#CCFF00" : r.percent >= 70 ? "#CCFF00" : r.percent >= 35 ? "#FFC107" : "#A3A3A3";
          return (
            <li key={r.student_id} className="py-3 flex items-center gap-3" data-testid={`ap-row-${r.student_id}`}>
              <div className="flex-1 min-w-0">
                <Link to={`/players/${r.student_id}`} className="text-sm font-semibold hover:underline truncate block">
                  {r.name}
                </Link>
                <div className="label-mono text-[10px] mt-0.5">
                  TARGET LEVEL {r.current_level} · {r.ticked} / {r.total} ticked
                </div>
              </div>
              <div className="w-32 shrink-0">
                <div className="h-1.5 bg-[#141414] relative overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${r.percent}%`, background: colour }} />
                </div>
              </div>
              <div className="h-display text-lg tracking-tight shrink-0 w-12 text-right" style={{ color: colour }}>
                {r.percent}%
              </div>
              {ready && (
                <span className="label-mono text-[10px] px-2 py-0.5 ml-1" style={{ background: "#CCFF00", color: "#0A0A0A", fontWeight: 700 }} data-testid={`ap-ready-${r.student_id}`}>
                  READY
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
